import { ConditionOperatorEnumType } from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { AddRelatedResourceCondition } from './AddRelatedResourceCondition';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { NestedGroupBlock } from './NestedGroupBlock';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderGroup,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

interface ConditionGroupProps {
  group: BuilderGroup;
  onUpdate: (group: BuilderGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
  group,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const { schemaCatalog } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const { contactFields } = schemaCatalog;
  const handleLogicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const logic = e.target.value === 'or' ? 'or' : 'and';
    onUpdate({ ...group, logic });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...group, description });
  };

  const handleConditionUpdate = (index: number, cond: BuilderCondition) => {
    onUpdate({ ...group, conditions: group.conditions.with(index, cond) });
  };

  const handleConditionDelete = (index: number) => {
    onUpdate({ ...group, conditions: group.conditions.toSpliced(index, 1) });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for intuitive nesting
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: group.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newNestedGroup],
    });
  };

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupHeader}>
        <div className={styles.nodeHeaderControls}>
          <span>Conditions combined with</span>
          <select
            className={styles.groupLogicSelect}
            value={group.logic}
            onChange={handleLogicChange}
            disabled={disabled}
            aria-label="Group logic">
            <option value="and">AND</option>
            <option value="or">OR</option>
          </select>
        </div>
        <div className={styles.nodeHeaderControls}>
          {group.description === undefined && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          <button
            type="button"
            className={styles.groupDeleteBtn}
            onClick={onDelete}
            disabled={disabled}
            aria-label="Delete group">
            Delete
          </button>
        </div>
      </div>

      {group.description !== undefined && (
        <div className={styles.groupDescription}>
          <ClauseDescriptionHeadline
            variant="group"
            description={group.description}
            onChange={handleDescriptionChange}
            disabled={disabled}
          />
        </div>
      )}

      <div className={styles.conditionsList}>
        {group.conditions.map((cond, i) => (
          <React.Fragment key={cond.id}>
            {i > 0 && (
              <div className={styles.andConnector}>
                <span className={styles.andConnectorLabel}>{group.logic.toUpperCase()}</span>
              </div>
            )}
            {isBuilderFieldCondition(cond) ? (
              <FieldConditionRow
                condition={cond}
                fieldOptions={contactFields}
                onUpdate={handleConditionUpdate.bind(null, i)}
                onDelete={handleConditionDelete.bind(null, i)}
                disabled={disabled}
              />
            ) : isBuilderRelatedResourceCondition(cond) ? (
              <RelatedResourceBlock
                condition={cond}
                onUpdate={handleConditionUpdate.bind(null, i)}
                onDelete={handleConditionDelete.bind(null, i)}
                disabled={disabled}
              />
            ) : isBuilderNestedGroup(cond) ? (
              <NestedGroupBlock
                condition={cond}
                onUpdate={handleConditionUpdate.bind(null, i)}
                onDelete={handleConditionDelete.bind(null, i)}
                disabled={disabled}
              />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.addConditionRow}>
        <button
          type="button"
          className={styles.addConditionBtn}
          onClick={handleAddFieldCondition}
          disabled={disabled || contactFields.length === 0}>
          + Add condition
        </button>
        <button
          type="button"
          className={styles.addConditionBtn}
          onClick={handleAddNestedGroup}
          disabled={disabled}>
          + Nested {group.logic === 'and' ? 'OR' : 'AND'} group
        </button>
        <AddRelatedResourceCondition
          subjectSchemaId={schemaCatalog.contactSchemaId}
          addRelatedResourceCondition={(newCond: BuilderRelatedResourceCondition) =>
            onUpdate({ ...group, conditions: [...group.conditions, newCond] })
          }
          disabled={disabled}
        />
      </div>
    </div>
  );
};
